import { ISelectionService } from '@src/slide/application/service/selection/selection-service.interface';
import {
    IAddGuideProps,
    IBaseGuideService,
} from '@src/slide/application/service/guides-manager/base-guides-service.interface';
import {
    EPpGuideOrientation,
    IGuide, IGuideModel,
} from '@src/slide/domain/ui-model/element-modeles/Interface/guide-model.interface';
import { convertCmToPx } from '@src/slide/infrastructure/module/independent/unit';
import { observable } from 'mobx';
import { DOMId } from '@editor/src/page/index/components/canvas/canvas-constants';
import { ICanvasSize } from '@src/slide/application/service/guides-manager/index.interface';
import {
    IGuideLineContainer,
} from '@src/slide/content-viewer/service/canvas-renderer/entry/base/feature/component/guide-component/interface/guide-line-container.interface';
import { Guide } from '@src/slide/domain/ui-model/element-modeles/guide-model';
import {
    DEFAULT_GUIDE_COLOR,
    DEFAULT_LAYOUT_GUIDE_COLOR,
    DEFAULT_MASTER_GUIDE_COLOR,
} from '@com/guides-line/guides-color-panel';

interface IBaseGuidesServiceProps {
    selectionService: ISelectionService;
}

export enum GuideEditType {
    slide,
    master,
    layout,
}

export abstract class BaseGuidesService implements IBaseGuideService {
    /**
     * 当前编辑页的参考线
     * horz没拼错，oo xml就这么拼的
     */
    @observable public vertGuides: Array<IGuide> = [];
    @observable public horzGuides: Array<IGuide> = [];
    public vertUiComponentContainer: IGuideLineContainer;
    public horzUiComponentContainer: IGuideLineContainer;
    protected slideWidth = 0;
    protected slideHeight = 0;
    protected scale = 1;
    protected left = 0;
    protected cLeft = 0;
    protected top = 0;
    protected cTop = 0;
    protected selectionService: ISelectionService;
    protected guideModel: IGuideModel;

    public constructor(props: IBaseGuidesServiceProps) {
        const { selectionService } = props;
        this.selectionService = selectionService;
    }

    public setGuideModel = (guideModel) => {
        this.guideModel = guideModel;
    };

    /**
     * 设置画布宽高
     * @param props
     */
    public setCanvasSize = (props: ICanvasSize) => {
        const { slideWidth, slideHeight, scale, left, cLeft, top, cTop } = props;
        this.slideWidth = slideWidth;
        this.slideHeight = slideHeight;
        this.scale = scale;
        this.left = left;
        this.cLeft = cLeft;
        this.top = top;
        this.cTop = cTop;
    };

    public setGuideColor = (color: string, guideModel: IGuide): void => {
        guideModel.color = color;
        this.horzUiComponentContainer.componentMap.get(guideModel)?.renderGuideLine(guideModel.pos, guideModel.color);
        this.vertUiComponentContainer.componentMap.get(guideModel)?.renderGuideLine(guideModel.pos, guideModel.color);
        this.updateGuidesProps();
    };

    public setGuidePos = (changedPos, guideModel: IGuide): void => {
        const { orient } = guideModel;
        guideModel.pos = Math.floor(changedPos);
        const guidesList = orient === EPpGuideOrientation.ppVerticalGuide ? this.vertGuides : this.horzGuides;
        const coverGuide = guidesList.filter(guide => guide.pos === guideModel.pos && guide !== guideModel)[0];
        this.deleteGuide(coverGuide);
        this.updateGuidesProps();
    };

    /**
     * 为空数据初始化参考线
     */
    public initGuidesIfEmptyData = (editType: GuideEditType): boolean => {
        let color;
        switch (editType) {
            case GuideEditType.slide:
                color = DEFAULT_GUIDE_COLOR;
                break;
            case GuideEditType.master:
                color = DEFAULT_MASTER_GUIDE_COLOR;
                break;
            case GuideEditType.layout:
                color = DEFAULT_LAYOUT_GUIDE_COLOR;
                break;
            default:
                color = DEFAULT_GUIDE_COLOR;
        }

        // 如果数据是空的，则初始化两个参考线
        if (!this.vertGuides.length && !this.horzGuides.length) {
            const vertGuideModel = new Guide();
            vertGuideModel.pos = Math.floor(this.slideWidth / 2);
            vertGuideModel.orient = EPpGuideOrientation.ppVerticalGuide;
            vertGuideModel.color = color;

            const horzGuideModel = new Guide();
            horzGuideModel.pos = Math.floor(this.slideHeight / 2);
            horzGuideModel.orient = EPpGuideOrientation.ppHorizontalGuide;
            horzGuideModel.color = color;

            this.vertGuides.push(vertGuideModel);
            this.horzGuides.push(horzGuideModel);
            this.updateGuidesProps();
            return false;
        }
        return true;
    };


    /**
     * 根据坐标查找参考线
     * @param pos
     * @param list
     * @private
     */
    protected findGuideModelByPos = (pos: number, list: Array<IGuide>) => list.find(guide => guide.pos === pos);

    /**
     * 计算新增垂直参考线的坐标
     * @private
     */
    protected computeAddVertPosition = (vertGuides: Array<IGuide>, option: IAddGuideProps) => {
        const { currentGuide, pageY = 0, pageX = 0 } = option;
        const addSize = Math.floor(convertCmToPx(0.5));
        let pos;
        if (currentGuide) {
            if (currentGuide.orient === EPpGuideOrientation.ppVerticalGuide) {
                pos = currentGuide.pos + addSize;
            } else {
                const containerDom = document.getElementById(DOMId.CANVAS);
                let current;
                if (containerDom) {
                    const rect = containerDom.getBoundingClientRect();
                    current = {
                        left: pageX + (containerDom?.scrollLeft || 0) - rect.left - window.pageXOffset,
                        top: pageY + (containerDom?.scrollTop || 0) - rect.top - window.pageYOffset,
                    };
                }
                pos = (current?.left - (this.cLeft + this.left)) / this.scale || Math.floor(this.slideWidth / 2);
            }
        } else {
            pos = Math.floor(this.slideWidth / 2);
        }
        while (this.findGuideModelByPos(Math.floor(pos), vertGuides)) {
            pos += addSize;
        }
        return pos;
    };

    /**
     * 计算新增水平参考线的坐标
     * @private
     */
    protected computeAddHorzPosition = (horzGuides: Array<IGuide>, option: IAddGuideProps) => {
        const { currentGuide, pageY = 0, pageX = 0 } = option;
        const addSize = Math.floor(convertCmToPx(0.5));
        let pos;
        if (currentGuide) {
            if (currentGuide.orient === EPpGuideOrientation.ppHorizontalGuide) {
                pos = currentGuide.pos + addSize;
            } else {
                const containerDom = document.getElementById(DOMId.CANVAS);
                let current;
                if (containerDom) {
                    const rect = containerDom.getBoundingClientRect();
                    current = {
                        left: pageX + (containerDom?.scrollLeft || 0) - rect.left - window.pageXOffset,
                        top: pageY + (containerDom?.scrollTop || 0) - rect.top - window.pageYOffset,
                    };
                }
                pos = (current?.top - (this.cTop + this.top)) / this.scale || Math.floor(this.slideHeight / 2);
            }
        } else {
            pos = Math.floor(this.slideHeight / 2);
        }
        while (this.findGuideModelByPos(Math.floor(pos), horzGuides)) {
            pos += addSize;
        }
        return pos;
    };

    protected fixVertPos(pos) {
        if (pos < 0) return 0;
        if (pos > this.slideWidth) return this.slideWidth;
        return pos;
    }

    protected fixHorzPos(pos) {
        if (pos < 0) return 0;
        if (pos > this.slideHeight) return this.slideHeight;
        return pos;
    }

    abstract updateGuidesProps(): void;

    abstract addHorzGuides(props: IAddGuideProps): void;

    abstract addVertGuides(props: IAddGuideProps): void;

    abstract deleteGuide(guide: IGuide): void;

    abstract updateGuidesPosWhenCanvasSizeChange(changeVertRadio: number, changeHorzRadio: number): void;

    abstract getUpdateProps(): Array<any>;
}
